import srApi from '@/services/api';

const api = srApi.create({ baseUrl: '/' });
import set from 'lodash/set';
import get from 'lodash/get';
import isEqual from "lodash/isEqual";

function filterDiff(a, b) {
  let result = [];

  Object.keys(a).forEach((key) => {
    if (!isEqual(a[key], b[key])) {
      result.push(key);
    }
  });

  return result;
}

export class SchoolSearchFilteringService {
  constructor(config, schools, needs) {
    this.config = config;
    this.schools = schools;
    this.needs = needs;
    this.appliedFilters = {};
    this.filterResults = {};
    this.handlers = {
      schoolName: async (filter) => await this.handleSchoolNameFilter(filter),
      location: async (filter) => await this.handleLocationFilter(filter),
      academics: async (filter) => await this.handleAcademicsFilter(filter),
      majors: async (filter) => await this.handleMajorsFilter(filter),
      athletics: async (filter) => await this.handleAthleticsFilter(filter),
      needs: async (filter) => await this.handleRosterNeedsFilter(filter),
    };
  }

  async filter(newFilters) {
    if (!newFilters || Object.keys(newFilters).length === 0) {
      return this.schools;
    }

    let changedFilters = filterDiff(newFilters, this.appliedFilters);

    this.appliedFilters = { ...newFilters }

    
    await Promise.all(changedFilters.map(async (filterKey) => {
      return await this.handlers[filterKey](newFilters[filterKey]);
    }));
    
    return this.calculateFilterResults();
  }

  calculateFilterResults() {

    let filtered = this.schools;
    Object.keys(this.appliedFilters).forEach(filter => {
      Object.keys(this.appliedFilters[filter]).forEach(subset => {
        const results = get(this.filterResults, `${filter}.${subset}`, []);
        filtered = filtered.filter(({ mongo_id }) => results.includes(mongo_id));
      })
    });

    return filtered;
  }

  async handleSchoolNameFilter(filter) {
    const response = await this.searchByKeyword(filter.schoolName);

    this.filterResults.schoolName = {
      schoolName: Object.keys(response),
    };
  }

  async handleLocationFilter(filter) {
    if (filter.regions) {
      const response = await this.searchByRegions(filter);

      set(this.filterResults, 'location.regions', Object.keys(response.data.results));
    }

    if (filter.distance) {
      if (!filter.distance.location) {
        set(this.filterResults, 'location.distance', []);
        return;
      }

      const response = await this.searchByDistance(filter);
      set(this.filterResults, 'location.distance', Object.keys(response.data.results));
    }
  }

  async handleMajorsFilter(filter) {
    const response = await this.searchByMajors(filter.majors);

    this.filterResults.majors = {
      majors: Object.keys(response.data),
    };
  }

  async handleAthleticsFilter(filter) {
    if (filter.divisions) {
      const response = await this.searchByDivisions(filter);
      set(this.filterResults, 'athletics.divisions', response.data.results.map(({ mongo_id }) => String(mongo_id)));
    }

    if (filter.conferences) {
      const response = await this.searchByConferences(filter);
      set(this.filterResults, 'athletics.conferences', response.data.results.map(({ mongo_id }) => String(mongo_id)));
    }
  }

  async searchByKeyword(filter) {
    return api.get(`/search/search_keyword/${filter}/${this.config.siteId}/${this.config.gender}`);
  }

  async handleAcademicsFilter(filter) {
    if (filter.selectivity) {
      const response = await this.searchBySelectivity(filter);

      set(this.filterResults, 'academics.selectivity', response.data.results.map(({ mongo_id }) => String(mongo_id)));
    }

    if (filter.actScore) {
      const response = await this.searchByACTMax(filter.actScore);
      set(this.filterResults, "academics.actScore", response.data.results.result.map(({ _id }) => String(_id)));
    }

    if (filter.satScore) {
      const response = await this.searchBySATMax(filter.satScore);
      set(this.filterResults, "academics.satScore", response.data.results.result.map(({ _id }) => String(_id)));
    }

    if (filter.enrollment) {
      const response = await this.searchBySize({
        sizes: filter.enrollment,
        programIds: this.schools.map(s => s.mongo_id)
      });
      set(this.filterResults, 'academics.enrollment', response.results.map(({ _id }) => String(_id)));
    }

    if (filter.acceptanceRate) {
      const response = await this.getSchoolsByAcceptanceRate(filter.acceptanceRate);

      set(this.filterResults, 'academics.acceptanceRate', response.data.results.result.map(({ _id }) => String(_id)));
    }
  }

  async searchByMajors(filter) {
    return api.postFormData('/search/schools_by_majors', {
      major_ids: filter,
    });
  }

  async searchByRegions(filter) {
    return api.postFormData(`/search/schools_by_regions`, {
      params: {
        regions: filter.regions.regions,
        states: filter.regions.states,
      },
    });
  }

  async searchByDistance(filter) {
    return api.postFormData('/search/schools_within', {
      params: {
        radius: filter.distance.radius,
        zip: filter.distance.zipcode,
        from: { ...filter.distance.location },
        unit: 'mi',
        only: { 'field': '_id', 'values': this.schools.map(s => s.mongo_id) },
      },
    });
  }

  async searchBySelectivity({ selectivity }) {
    return api.postFormData('/search/mysqlSearch', {
      fields: ['c.mongo_id'],
      search: 'advanced',
      selectivity,
    });
  }

  async searchByDivisions({ divisions }) {
    return api.postFormData('/search/mysqlSearch', {
      fields: ['c.mongo_id'],
      search: 'advanced',
      division: divisions,
    });
  }

  async searchByConferences({ conferences }) {
    return api.postFormData('/search/mysqlSearch', {
      fields: ['c.mongo_id'],
      search: 'advanced',
      conference: conferences,
    });
  }

  async searchBySize({ sizes, programIds }) {
    return await api.post('/search/schools_by_sizes', {
      params: {
        only: {
          field: '_id',
          values: programIds,
        },
        size: sizes,
      },
    });
  }

  async getSchoolsByAcceptanceRate({ max, min }) {
    return await api.postFormData('/search/acceptance_rate_between', {
      acceptance_rate: {
        gte: min ?? 0,
        lte: max ?? 100,
      },
    });
  }

  /**
   * Search schools by ACT value range.
   * @param {number} actMax - The max ACT value to search schools by.
   * @return {Promise}
   */
  async searchByACTMax(actMax) {
    return await api.postFormData('/search/act_total_between', {
      act_request: {
        value: [actMax]
      }
    });
  }

  /**
   * Search schools by SAT value range.
   * @param {number} satMax - The max SAT value to search schools by.
   * @returns {Promise}
   */
  async searchBySATMax(satMax) {
    return await api.postFormData('/search/sat_total_between', {
      sat_request: {
        value: [satMax]
      }
    });
  }

  async handleRosterNeedsFilter(filters) {
    const needsFilters = filters.needs.filter(filter => filter.includes('.'));
    let needs = [];

    needsFilters.forEach((filter) => this.applyFilters(filter, needs));

    needs = [...(new Set(needs.filter(f => f).map(f => `${f}`)))];
    this.filterResults.needs = { needs };
  }

  applyFilters(filter, needs) {
      needs.push(...(get(this.needs, filter) ?? []));
      const splitPath = filter.split('.');
      const isAllRosterSelection = splitPath[0] === 'non-match';
      splitPath.shift();
      const path = splitPath.join('.');

      if(isAllRosterSelection) {
        needs.push(...(get(this.needs, `full.${path}`) ?? []));
        needs.push(...(get(this.needs, `potential.${path}`) ?? []));
      }
  }
  
}

export default new SchoolSearchFilteringService();